import React from "react";
import {
  EventApi,
  DateSelectArg,
  EventClickArg,
  EventContentArg,
  EventInput,
  formatDate,
} from "@fullcalendar/core";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import {
  deleteAppointment,
  readOrgAppointments,
  updateAppointment,
} from "services/appointments.service";
import vgToast from "utils/toast";
import BookingDialog, { BookingSubmission } from "./BookingDialog";
import FreeTimesPanel from "./FreeTimesPanel";
import { createBooking } from "services/bookings.service";
import "./calendar.css";

/**
 * What a drop and a resize have in common. FullCalendar types them separately -
 * a drop also carries how far it moved - so one handler for both has to ask for
 * the part they share.
 */
type EventChange = { event: EventApi; revert: () => void };

/** Local wall time in the shape <input type="datetime-local"> expects. */
function toDateTimeLocal(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

function renderEventContent(eventContent: EventContentArg) {
  return (
    <div className="flex flex-col gap-0.5 overflow-hidden text-left leading-tight">
      {eventContent.timeText ? (
        <span className="text-[0.7rem] font-semibold opacity-95">
          {eventContent.timeText}
        </span>
      ) : null}
      <span className="truncate font-medium">{eventContent.event.title}</span>
    </div>
  );
}

function sortEventsByStart(events: EventApi[]): EventApi[] {
  return [...events].sort((a, b) => {
    const ta = a.start?.getTime() ?? 0;
    const tb = b.start?.getTime() ?? 0;
    return ta - tb;
  });
}

function TimelineEvent({
  event,
  isLast,
}: {
  event: EventApi;
  isLast: boolean;
}) {
  const start = event.start;
  if (!start) return null;

  const dateLine = formatDate(start, {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const timeLine = event.allDay
    ? "All day"
    : formatDate(start, {
        hour: "numeric",
        minute: "2-digit",
      });

  return (
    <li className="flex gap-3 pb-5 last:pb-0">
      <div className="flex w-4 shrink-0 flex-col items-center">
        <span
          className="z-10 h-2.5 w-2.5 rounded-full border-2 border-white bg-vg-blue ring-1 ring-vg-blue/20"
          aria-hidden
        />
        {!isLast && (
          <span className="mt-1 w-px min-h-[2.5rem] bg-slate-200" aria-hidden />
        )}
      </div>
      <div className="min-w-0 flex-1 pb-1">
        <p className="text-sm font-medium text-slate-900">{event.title}</p>
        <p className="mt-0.5 text-xs text-slate-500">{dateLine}</p>
        <p className="text-xs text-slate-500">{timeLine}</p>
      </div>
    </li>
  );
}

export default function CalendarView() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId, loading: orgLoading } = useActiveOrg();

  const [weekendsVisible, setWeekendsVisible] = React.useState(true);
  const [currentEvents, setCurrentEvents] = React.useState<EventApi[]>([]);
  const [events, setEvents] = React.useState<EventInput[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [bookingOpen, setBookingOpen] = React.useState(false);
  const [bookingSaving, setBookingSaving] = React.useState(false);
  const [bookingRange, setBookingRange] = React.useState({ start: "", end: "" });

  /**
   * The calendar previously rendered INITIAL_EVENTS - two hardcoded demo events
   * titled "All-day event" and "Timed event" - and every edit lived in
   * FullCalendar's in-memory state, so nothing survived a refresh. Appointments
   * are now loaded from and written to the API.
   */
  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgLoading || orgId <= 0) {
      setEvents([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const token = await getAccessTokenSilently();
      const { data, error: apiError } = await readOrgAppointments(token, orgId);
      if (apiError) {
        setError(apiError.message);
        setEvents([]);
        return;
      }
      setEvents(
        (data ?? []).map((appt) => ({
          id: String(appt.appointmentId),
          title: appt.appointmentTitle || "(untitled)",
          start: appt.start || undefined,
          end: appt.end || undefined,
          allDay: appt.allDay,
          // Carried so a drag can save without blanking them: an update writes
          // the whole appointment, and the calendar event has nowhere else to
          // keep what it is not displaying.
          extendedProps: {
            appointmentType: appt.appointmentType,
            contactId: appt.contactId,
          },
        }))
      );
    } catch (e) {
      setError((e as Error).message);
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId, orgLoading]);

  React.useEffect(() => {
    load();
  }, [load]);

  /**
   * Dragging a range opens the booking dialog rather than prompting for a title.
   *
   * A calendar entry made by staff is a booking: a client, optionally a patient,
   * and what is being booked. The previous window.prompt could only produce a
   * titled appointment with nothing attached to it, which is not something a
   * clinic can work from.
   *
   * Plain appointments are still creatable from the clinic's appointments tab -
   * this is the booking entry point.
   */
  const handleDateSelect = React.useCallback((selectInfo: DateSelectArg) => {
    selectInfo.view.calendar.unselect();

    if (orgId <= 0) {
      vgToast.error("Choose an active provider before making a booking.");
      return;
    }

    // datetime-local wants local wall time with no zone, which is exactly what
    // FullCalendar's startStr already is for a timed selection. The service
    // converts to UTC on the way out.
    setBookingRange({
      start: toDateTimeLocal(selectInfo.start),
      end: toDateTimeLocal(selectInfo.end),
    });
    setBookingOpen(true);
  }, [orgId]);

  const handleBookingSubmit = React.useCallback(
    async (values: BookingSubmission) => {
      setBookingSaving(true);
      try {
        const token = await getAccessTokenSilently();
        const { error: apiError } = await createBooking(token, {
          orgId,
          contactId: values.contactId,
          animalId: values.animalId,
          serviceId: values.serviceId,
          serviceFulfilmentConfigId: values.serviceFulfilmentConfigId,
          start: new Date(values.start).toISOString(),
          end: new Date(values.end).toISOString(),
        });
        if (apiError) {
          vgToast.error(apiError.message);
          return;
        }
        vgToast.success("Booking created.");
        setBookingOpen(false);
        // Reload so the appointment the API created for this booking appears.
        await load();
      } catch (e) {
        vgToast.error((e as Error).message);
      } finally {
        setBookingSaving(false);
      }
    },
    [getAccessTokenSilently, orgId, load]
  );

  const handleEventClick = React.useCallback(
    async (clickInfo: EventClickArg) => {
      if (
        !window.confirm(
          `Delete “${clickInfo.event.title}”? This cannot be undone.`
        )
      ) {
        return;
      }

      const appointmentId = Number(clickInfo.event.id);
      if (!Number.isFinite(appointmentId) || appointmentId <= 0) {
        vgToast.error("This event has no appointment id and cannot be deleted.");
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const { error: apiError } = await deleteAppointment(
          token,
          orgId,
          appointmentId
        );
        if (apiError) {
          vgToast.error(apiError.message);
          return;
        }
        await load();
      } catch (e) {
        vgToast.error((e as Error).message);
      }
    },
    [getAccessTokenSilently, orgId, load]
  );

  /**
   * A dragged or resized appointment, saved.
   *
   * The calendar is `editable`, so both gestures work whether or not anything
   * handles them. Unhandled, the move was only ever visual - it looked saved,
   * and the next reload silently put it back, which is worse than not letting
   * anyone drag at all.
   *
   * A failed save puts the appointment back where it was. Leaving it where it
   * was dropped would show a time the clinic does not actually have.
   */
  const handleEventMove = React.useCallback(
    async (changeInfo: EventChange) => {
      const { event } = changeInfo;
      const appointmentId = Number(event.id);
      if (!Number.isFinite(appointmentId) || appointmentId <= 0) {
        vgToast.error("This event has no appointment id and cannot be moved.");
        changeInfo.revert();
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const { error: apiError } = await updateAppointment(token, {
          orgId,
          appointmentId,
          appointmentTitle: event.title,
          appointmentType: Number(event.extendedProps?.appointmentType ?? 0),
          start: event.start?.toISOString(),
          end: event.end?.toISOString(),
          allDay: event.allDay,
          contactId: Number(event.extendedProps?.contactId ?? 0),
        });
        if (apiError) {
          vgToast.error(apiError.message);
          changeInfo.revert();
        }
      } catch (e) {
        vgToast.error((e as Error).message);
        changeInfo.revert();
      }
    },
    [getAccessTokenSilently, orgId]
  );

  const handleEvents = React.useCallback((nextEvents: EventApi[]) => {
    setCurrentEvents(nextEvents);
  }, []);

  const sortedEvents = React.useMemo(
    () => sortEventsByStart(currentEvents),
    [currentEvents]
  );

  return (
    <div className="calendar-page flex min-h-0 flex-1 flex-col gap-4">
      {error && (
        <div className="shrink-0 rounded-[10px] border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-600">
          Could not load appointments: {error}
        </div>
      )}

      <div className="flex shrink-0 items-center justify-between gap-4">
        <p className="text-sm text-slate-500">
          {loading ? "Loading appointments…" : ""}
        </p>
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-slate-300 accent-vg-blue focus:ring-2 focus:ring-vg-blue/30"
            checked={weekendsVisible}
            onChange={(e) => setWeekendsVisible(e.target.checked)}
          />
          Show weekends
        </label>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 lg:flex-row lg:items-stretch lg:gap-5">
        <div className="flex min-h-[28rem] min-w-0 flex-1 flex-col lg:min-h-0">
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-slate-200 bg-white">
            <div className="calendar-fc-host min-h-0 flex-1 p-3 sm:p-4">
              <FullCalendar
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                headerToolbar={{
                  left: "prev,next today",
                  center: "title",
                  right: "dayGridMonth,timeGridWeek,timeGridDay",
                }}
                initialView="dayGridMonth"
                editable
                selectable
                selectMirror
                dayMaxEvents
                weekends={weekendsVisible}
                height="100%"
                events={events}
                eventColor="#6F98C8"
                eventTextColor="#ffffff"
                select={handleDateSelect}
                eventContent={renderEventContent}
                eventClick={handleEventClick}
                eventDrop={handleEventMove}
                eventResize={handleEventMove}
                eventsSet={handleEvents}
              />
            </div>
          </div>
        </div>

        <aside className="flex w-full shrink-0 flex-col rounded-[10px] border border-slate-200 bg-white lg:min-h-0 lg:w-80 lg:max-w-sm">
          <FreeTimesPanel
            orgId={orgId}
            onPick={(start, end) => {
              setBookingRange({ start: toDateTimeLocal(start), end: toDateTimeLocal(end) });
              setBookingOpen(true);
            }}
          />
          <div className="border-b border-slate-100 px-4 py-3">
            <h2 className="text-sm font-semibold tracking-tight text-slate-900">
              Events timeline
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {currentEvents.length === 0
                ? "No events on this calendar"
                : `${currentEvents.length} event${
                    currentEvents.length === 1 ? "" : "s"
                  }, soonest first`}
            </p>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
            {sortedEvents.length === 0 ? (
              <p className="text-sm leading-relaxed text-slate-500">
                Select dates in the calendar to add an event. They will appear
                here in order.
              </p>
            ) : (
              <ul className="m-0 list-none p-0">
                {sortedEvents.map((event, index) => (
                  <TimelineEvent
                    key={event.id}
                    event={event}
                    isLast={index === sortedEvents.length - 1}
                  />
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>

      <BookingDialog
        open={bookingOpen}
        initialStart={bookingRange.start}
        initialEnd={bookingRange.end}
        busy={bookingSaving}
        onCancel={() => {
          if (!bookingSaving) setBookingOpen(false);
        }}
        onSubmit={handleBookingSubmit}
      />
    </div>
  );
}
